import {
  INTRANET_COMMENT_GIF_MAX_BYTES,
  INTRANET_FILE_MAX_BYTES,
  INTRANET_IMAGE_MAX_BYTES,
  INTRANET_IMAGE_MAX_MB,
  INTRANET_KNOWLEDGE_FILE_MAX_BYTES,
  intranetImageMaxBytesFromMb,
  intranetImageMaxMb,
} from '@gb-ticket/shared-types';
import { ArrowLeftIcon } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useBeforeUnload, useBlocker } from 'react-router';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { intranetPanelClassName } from '@/products/intranet/features/intranet/intranet-panel';
import {
  useIntranetAppearance,
  useUpdateIntranetAppearance,
} from '@/products/intranet/features/intranet/use-intranet';
import { INTRANET_EDITORIAL_PATH } from '@/products/intranet/intranet-paths';

const CONTENT_IMAGE_MAX_MB = Math.floor(INTRANET_FILE_MAX_BYTES / (1024 * 1024));
const KNOWLEDGE_FILE_MAX_MB = Math.floor(INTRANET_KNOWLEDGE_FILE_MAX_BYTES / (1024 * 1024));

function isStoredAppearance(updatedAt: string | undefined): boolean {
  return updatedAt !== undefined && new Date(updatedAt).getTime() > 0;
}

export function SettingsPage() {
  usePageTitle('Einstellungen');
  const appearance = useIntranetAppearance();
  const update = useUpdateIntranetAppearance();
  const current = appearance.data;
  const [coverMaxMb, setCoverMaxMb] = useState<number | null>(null);
  const [contentMaxMb, setContentMaxMb] = useState<number | null>(null);
  const [gifMaxMb, setGifMaxMb] = useState<number | null>(null);
  const [knowledgeMaxMb, setKnowledgeMaxMb] = useState<number | null>(null);
  const [coverError, setCoverError] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [gifError, setGifError] = useState<string | null>(null);
  const [knowledgeError, setKnowledgeError] = useState<string | null>(null);

  const storedCoverMb = intranetImageMaxMb(current?.imageMaxBytes ?? INTRANET_IMAGE_MAX_BYTES);
  const storedContentMb = intranetImageMaxMb(
    current?.contentImageMaxBytes ?? INTRANET_IMAGE_MAX_BYTES,
  );
  const storedGifMb = intranetImageMaxMb(
    current?.commentGifMaxBytes ?? INTRANET_COMMENT_GIF_MAX_BYTES,
  );
  const storedKnowledgeMb = intranetImageMaxMb(
    current?.knowledgeFileMaxBytes ?? INTRANET_FILE_MAX_BYTES,
  );
  const shownCoverMb = coverMaxMb ?? storedCoverMb;
  const shownContentMb = contentMaxMb ?? storedContentMb;
  const shownGifMb = gifMaxMb ?? storedGifMb;
  const shownKnowledgeMb = knowledgeMaxMb ?? storedKnowledgeMb;
  const dirty =
    appearance.isSuccess &&
    (shownCoverMb !== storedCoverMb ||
      shownContentMb !== storedContentMb ||
      shownGifMb !== storedGifMb ||
      shownKnowledgeMb !== storedKnowledgeMb);
  const saving = update.isPending;
  const blocker = useBlocker(dirty);

  useBeforeUnload(
    (event) => {
      if (dirty) {
        event.preventDefault();
      }
    },
    { capture: true },
  );

  useEffect(() => {
    if (!dirty && blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [dirty, blocker]);

  function validate(): boolean {
    let ok = true;
    if (
      !Number.isInteger(shownCoverMb) ||
      shownCoverMb < 1 ||
      shownCoverMb > INTRANET_IMAGE_MAX_MB
    ) {
      setCoverError(`Bitte eine ganze Zahl von 1 bis ${INTRANET_IMAGE_MAX_MB} eingeben.`);
      ok = false;
    } else {
      setCoverError(null);
    }
    if (
      !Number.isInteger(shownContentMb) ||
      shownContentMb < 1 ||
      shownContentMb > CONTENT_IMAGE_MAX_MB
    ) {
      setContentError(`Bitte eine ganze Zahl von 1 bis ${CONTENT_IMAGE_MAX_MB} eingeben.`);
      ok = false;
    } else {
      setContentError(null);
    }
    if (!Number.isInteger(shownGifMb) || shownGifMb < 1 || shownGifMb > CONTENT_IMAGE_MAX_MB) {
      setGifError(`Bitte eine ganze Zahl von 1 bis ${CONTENT_IMAGE_MAX_MB} eingeben.`);
      ok = false;
    } else {
      setGifError(null);
    }
    if (
      !Number.isInteger(shownKnowledgeMb) ||
      shownKnowledgeMb < 1 ||
      shownKnowledgeMb > KNOWLEDGE_FILE_MAX_MB
    ) {
      setKnowledgeError(`Bitte eine ganze Zahl von 1 bis ${KNOWLEDGE_FILE_MAX_MB} eingeben.`);
      ok = false;
    } else {
      setKnowledgeError(null);
    }
    return ok;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!validate()) {
      return;
    }
    try {
      await update.mutateAsync({
        imageMaxBytes: intranetImageMaxBytesFromMb(shownCoverMb),
        contentImageMaxBytes: intranetImageMaxBytesFromMb(shownContentMb),
        commentGifMaxBytes: intranetImageMaxBytesFromMb(shownGifMb),
        knowledgeFileMaxBytes: intranetImageMaxBytesFromMb(shownKnowledgeMb),
      });
      setCoverMaxMb(null);
      setContentMaxMb(null);
      setGifMaxMb(null);
      setKnowledgeMaxMb(null);
      toast.success('Einstellungen gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Einstellungen"
        description="Grenzen für Titelbilder, Bilder in Beiträgen, GIFs in Kommentaren und Dateien in der Wissensdatenbank."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to={INTRANET_EDITORIAL_PATH}>
              <ArrowLeftIcon aria-hidden />
              Zurück
            </Link>
          </Button>
        }
      />

      {appearance.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {appearance.error instanceof ApiRequestError
            ? appearance.error.message
            : 'Die Einstellungen konnten nicht geladen werden.'}
        </p>
      ) : null}

      <form
        className={`${intranetPanelClassName} grid max-w-lg gap-3`}
        noValidate
        onSubmit={(event) => void onSubmit(event)}
      >
        {appearance.isPending ? (
          <p className="text-sm text-muted-foreground">Wird geladen …</p>
        ) : null}
        {current && isStoredAppearance(current.updatedAt) ? (
          <p className="text-sm text-muted-foreground">
            Zuletzt gespeichert: {formatDateTime(current.updatedAt)}
          </p>
        ) : null}
        <div className="space-y-1">
          <Label htmlFor="settings-cover-max">Titelbilder höchstens (MB)</Label>
          <Input
            id="settings-cover-max"
            type="number"
            inputMode="numeric"
            min={1}
            max={INTRANET_IMAGE_MAX_MB}
            step={1}
            value={shownCoverMb}
            aria-invalid={coverError !== null}
            aria-describedby="settings-cover-help"
            disabled={appearance.isPending || appearance.isError}
            onChange={(event) => {
              setCoverMaxMb(Number(event.target.value) || 0);
              setCoverError(null);
            }}
            required
          />
          <p id="settings-cover-help" className="text-xs text-muted-foreground">
            Ganze Megabyte. Gilt für das Titelbild, das Hintergrundbild, Modul-Logos und Fotos im
            Bautagebuch. Höchstens {INTRANET_IMAGE_MAX_MB} MB, darunter liegt die Servergrenze von
            12 MB. Titelvideos bleiben unter 12 MB.
          </p>
          {coverError ? (
            <p role="alert" className="text-sm text-destructive">
              {coverError}
            </p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="settings-content-max">Bilder in Beiträgen höchstens (MB)</Label>
          <Input
            id="settings-content-max"
            type="number"
            inputMode="numeric"
            min={1}
            max={CONTENT_IMAGE_MAX_MB}
            step={1}
            value={shownContentMb}
            aria-invalid={contentError !== null}
            aria-describedby="settings-content-help"
            disabled={appearance.isPending || appearance.isError}
            onChange={(event) => {
              setContentMaxMb(Number(event.target.value) || 0);
              setContentError(null);
            }}
            required
          />
          <p id="settings-content-help" className="text-xs text-muted-foreground">
            Ganze Megabyte. Gilt für JPEG, PNG und WebP im Beitragstext, als Anhang, in der
            Wissensdatenbank und im Chat. Höchstens {CONTENT_IMAGE_MAX_MB} MB.
          </p>
          {contentError ? (
            <p role="alert" className="text-sm text-destructive">
              {contentError}
            </p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="settings-gif-max">GIFs in Kommentaren höchstens (MB)</Label>
          <Input
            id="settings-gif-max"
            type="number"
            inputMode="numeric"
            min={1}
            max={CONTENT_IMAGE_MAX_MB}
            step={1}
            value={shownGifMb}
            aria-invalid={gifError !== null}
            aria-describedby="settings-gif-help"
            disabled={appearance.isPending || appearance.isError}
            onChange={(event) => {
              setGifMaxMb(Number(event.target.value) || 0);
              setGifError(null);
            }}
            required
          />
          <p id="settings-gif-help" className="text-xs text-muted-foreground">
            Ganze Megabyte. Gilt nur für GIFs unter Nachrichten-Kommentaren. JPEG, PNG und WebP in
            Kommentaren nutzen die Grenze für Bilder in Beiträgen. Höchstens {CONTENT_IMAGE_MAX_MB}{' '}
            MB.
          </p>
          {gifError ? (
            <p role="alert" className="text-sm text-destructive">
              {gifError}
            </p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="settings-knowledge-max">
            Dateien in der Wissensdatenbank höchstens (MB)
          </Label>
          <Input
            id="settings-knowledge-max"
            type="number"
            inputMode="numeric"
            min={1}
            max={KNOWLEDGE_FILE_MAX_MB}
            step={1}
            value={shownKnowledgeMb}
            aria-invalid={knowledgeError !== null}
            aria-describedby="settings-knowledge-help"
            disabled={appearance.isPending || appearance.isError}
            onChange={(event) => {
              setKnowledgeMaxMb(Number(event.target.value) || 0);
              setKnowledgeError(null);
            }}
            required
          />
          <p id="settings-knowledge-help" className="text-xs text-muted-foreground">
            Ganze Megabyte. Gilt für PDF, Office, Text, Video und ZIP auf Seiten der
            Wissensdatenbank. JPEG, PNG und WebP bleiben bei der Grenze für Bilder in Beiträgen.
            Höchstens {KNOWLEDGE_FILE_MAX_MB} MB.
          </p>
          {knowledgeError ? (
            <p role="alert" className="text-sm text-destructive">
              {knowledgeError}
            </p>
          ) : null}
        </div>
        <Button type="submit" disabled={saving || appearance.isPending || appearance.isError}>
          {saving ? 'Wird gespeichert …' : 'Speichern'}
        </Button>
      </form>

      <AlertDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(next) => {
          if (!next && blocker.state === 'blocked') {
            blocker.reset();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Änderungen verwerfen?</AlertDialogTitle>
            <AlertDialogDescription>Die Größen sind noch nicht gespeichert.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                if (blocker.state === 'blocked') {
                  blocker.reset();
                }
              }}
            >
              Bleiben
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                setCoverMaxMb(null);
                setContentMaxMb(null);
                setGifMaxMb(null);
                setKnowledgeMaxMb(null);
                if (blocker.state === 'blocked') {
                  blocker.proceed();
                }
              }}
            >
              Verwerfen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
